import { Button } from '@/components/Buttons';
import { BUTTON_LABELS } from '@/constants';
import { createPortal } from 'react-dom';
import { SlideoutModalProps } from './types';
import { useSlideoutModalFeature } from '@/features/slideout-modal/useSlideoutModalFeature';
import React, { useId } from 'react';

import {
  slideoutModalBackdropVariants,
  slideoutModalCloseButtonVariants,
  slideoutModalContainerVariants,
  slideoutModalHeaderVariants,
  slideoutModalPanelBodyStyles,
  slideoutModalPanelVariants,
  slideoutModalTitleStyles,
} from './SlideoutModal.styles';

/**
 * SlideoutModal
 *
 * A collapsible sidebar slideout modal component that slides
 * in from the side of the screen.
 */
export const SlideoutModal: React.FC<SlideoutModalProps> = ({
  autoFocus = true,
  children,
  className,
  closeButtonIconOnly = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  isOpen,
  maxWidth = '958px',
  onClose,
  side = 'right',
  title,
  titleOverride,
}) => {
  // ==================================================================
  // Feature Hook
  // ==================================================================
  const { buttonRef, handleBackdropClick, modalRef, mounted } = useSlideoutModalFeature({
    autoFocus,
    closeOnBackdropClick,
    closeOnEscape,
    isOpen,
    onClose,
  });

  // ==================================================================
  // IDs for ARIA attributes
  // ==================================================================
  const dialogDescriptionId = useId();
  const dialogTitleId = useId();

  // ==================================================================
  // Render nothing until mounted to ensure portal can attach to document.body
  // ==================================================================
  if (!mounted) {
    return null;
  }

  // ==================================================================
  // Class names (cva)
  // ==================================================================
  const _showTitle = Boolean(title || titleOverride);
  const backdropClassNames = slideoutModalBackdropVariants({ isOpen });
  const classNames = slideoutModalContainerVariants({ isOpen, side });
  const closeButtonClassNames = slideoutModalCloseButtonVariants({ hasTitle: _showTitle });
  const defaultTitleClassNames = slideoutModalTitleStyles;
  const modalPanelClassNames = slideoutModalPanelVariants({ className, isOpen, side });
  const panelBodyClassNames = slideoutModalPanelBodyStyles;
  const panelHeaderClassNames = slideoutModalHeaderVariants({ hasTitle: _showTitle });

  // ==================================================================
  // ARIA label strategy
  // ==================================================================

  // ARIA label strategy:
  // - Prefer aria-labelledby when we render the built-in <h2> title
  // - Fallback to aria-label when title is custom (titleOverride) or absent
  const ariaLabelledBy = _showTitle && !titleOverride ? dialogTitleId : undefined;
  const ariaLabel = ariaLabelledBy ? undefined : title || 'Slideout panel';

  // ==================================================================
  // Render
  // ==================================================================
  const modalContent = (
    <div className={classNames} onClick={handleBackdropClick} role="presentation">
      <div aria-hidden="true" className={backdropClassNames} role="presentation" />
      <div
        aria-describedby={isOpen ? dialogDescriptionId : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-modal={isOpen ? 'true' : undefined}
        className={modalPanelClassNames}
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        role={isOpen ? 'dialog' : undefined}
        style={{ maxWidth }}
        tabIndex={-1}
      >
        {/* Header */}
        <div className={panelHeaderClassNames}>
          {_showTitle && (
            <div className="flex flex-col justify-center">
              {titleOverride ? (
                titleOverride
              ) : (
                <h2 id={dialogTitleId} className={defaultTitleClassNames}>
                  {title}
                </h2>
              )}
            </div>
          )}
          <Button
            className={closeButtonClassNames}
            forwardRef={buttonRef}
            iconLeft="X"
            iconOnly={closeButtonIconOnly}
            label={BUTTON_LABELS.CLOSE}
            onClick={onClose}
            aria-label={BUTTON_LABELS.CLOSE}
            size="small"
            tabIndex={0}
            variant="tertiary"
          />
        </div>

        {/* Body */}
        <div className={panelBodyClassNames} id={dialogDescriptionId} role="document">
          {children}
        </div>
      </div>
    </div>
  );

  // ==================================================================
  // Render the modal content into a portal
  // ==================================================================
  const portalRoot = document.getElementById('portal-root') ?? document.body;
  return createPortal(modalContent, portalRoot);
};

SlideoutModal.displayName = 'SlideoutModal';

export default SlideoutModal;
